import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Phone,
  Navigation,
  ArrowLeft,
  Clock,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Truck,
  User,
} from "lucide-react";
import { authApi, formatApiError } from "@/lib/auth";

const STATUS_COLORS = {
  scheduled: "bg-[#fffbf0] text-[#ff6b35] border border-[#ff6b35]/40",
  in_progress: "bg-[#ff6b35] text-white",
  completed: "bg-[#1a1a1a] text-white",
  cancelled: "bg-white text-[#333] border border-[#333]/30",
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const slotStartMinutes = (slot = "") => {
  const m = slot.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return 99999;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (m[3].toUpperCase() === "PM" && h !== 12) h += 12;
  if (m[3].toUpperCase() === "AM" && h === 12) h = 0;
  return h * 60 + min;
};

export default function AdminTodayRun({ admin }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date] = useState(todayISO());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authApi.get("/admin/bookings", { params: { date } });
      const sorted = (data.bookings || [])
        .slice()
        .sort((a, b) => slotStartMinutes(a.pickup_time_slot) - slotStartMinutes(b.pickup_time_slot));
      setBookings(sorted);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quickStatus = async (id, newStatus) => {
    try {
      await authApi.patch(`/admin/bookings/${id}`, { status: newStatus });
      toast.success(`Marked ${newStatus.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totals = useMemo(() => {
    const out = { total: bookings.length, scheduled: 0, in_progress: 0, completed: 0 };
    bookings.forEach((b) => {
      if (b.status in out) out[b.status] += 1;
    });
    return out;
  }, [bookings]);

  const dateLabel = new Date(date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div data-testid="today-run-page" className="min-h-screen bg-[#fffbf0]">
      <header className="bg-[#1a1a1a] text-white sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/admin/dashboard" data-testid="back-to-dashboard" className="text-white/80 hover:text-white inline-flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-8 w-8 rounded-lg bg-[#ff6b35]">
              <Truck size={16} strokeWidth={2.5} />
            </span>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-sm">Today's Run</div>
              <div className="text-white/60 text-[10px] uppercase tracking-widest">{dateLabel}</div>
            </div>
          </div>
          <Button
            data-testid="today-refresh"
            onClick={load}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10 hover:text-white"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5">
        {/* Totals */}
        <div data-testid="today-totals" className="grid grid-cols-4 gap-2 sm:gap-3 mb-5">
          <StatPill label="Total" value={totals.total} />
          <StatPill label="To do" value={totals.scheduled} highlight />
          <StatPill label="In progress" value={totals.in_progress} accent />
          <StatPill label="Done" value={totals.completed} dark />
        </div>

        {loading ? (
          <div className="grid place-items-center py-20">
            <Loader2 className="animate-spin text-[#ff6b35]" size={28} />
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-[#1a1a1a]/20 rounded-xl p-10 text-center">
            <div className="font-display font-extrabold text-xl">Nothing on the schedule today</div>
            <div className="text-[#333] text-sm mt-1">Take a break — or go knock on some doors.</div>
          </div>
        ) : (
          <ul className="space-y-3">
            {bookings.map((b, idx) => (
              <RunCard key={b.id} booking={b} index={idx + 1} onStatus={quickStatus} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatPill({ label, value, highlight, accent, dark }) {
  const cls = dark
    ? "bg-[#1a1a1a] text-white"
    : accent
    ? "bg-[#ff6b35] text-white"
    : highlight
    ? "bg-white border-2 border-[#ff6b35] text-[#1a1a1a]"
    : "bg-white border border-[#1a1a1a]/10 text-[#1a1a1a]";
  return (
    <div className={`${cls} rounded-xl p-3 text-center`}>
      <div className="font-display text-2xl sm:text-3xl font-black leading-none">{value}</div>
      <div className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mt-1 opacity-80">{label}</div>
    </div>
  );
}

function RunCard({ booking, index, onStatus }) {
  const mapsHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${booking.address}, ${booking.city}, OH ${booking.zip_code}`
  )}`;
  const telHref = `tel:${booking.phone.replace(/[^\d+]/g, "")}`;

  const done = booking.status === "completed" || booking.status === "cancelled";

  return (
    <li data-testid={`run-card-${booking.id}`} className={`bg-white rounded-xl border-l-[6px] ${done ? "border-[#1a1a1a] opacity-75" : "border-[#ff6b35]"} shadow-sm overflow-hidden`}>
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="grid place-items-center flex-shrink-0 w-10 h-10 rounded-full bg-[#1a1a1a] text-white font-display font-black text-lg">
            {index}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-display font-extrabold text-lg leading-tight">{booking.customer_name}</span>
              <Badge className={`${STATUS_COLORS[booking.status] || ""} text-[10px] font-bold uppercase`}>
                {booking.status.replace("_", " ")}
              </Badge>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-sm text-[#1a1a1a]">
              <Clock size={13} className="text-[#ff6b35]" />
              <span className="font-semibold">{booking.pickup_time_slot}</span>
              <span className="text-[#333]/60">·</span>
              <span className="text-[#333]">${booking.total_price?.toFixed?.(0) ?? booking.total_price} · {booking.tier.replace("_", " ")}</span>
            </div>
            {booking.assigned_staff_name ? (
              <div className="mt-0.5 text-xs text-[#333] flex items-center gap-1.5">
                <User size={12} className="text-[#ff6b35]" /> {booking.assigned_staff_name}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-3 text-sm text-[#1a1a1a]">
          <div className="font-semibold">{booking.address}</div>
          <div className="text-[#333]">{booking.city}, OH {booking.zip_code} · stage at <span className="font-semibold">{booking.staging_location}</span></div>
        </div>

        {booking.notes ? (
          <div className="mt-2 text-xs text-[#333] bg-[#fffbf0] rounded px-2 py-1.5 border-l-2 border-[#ff6b35]">
            Note: {booking.notes}
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <a
            data-testid={`navigate-${booking.id}`}
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold rounded-lg px-3 py-2.5 text-sm transition-colors"
          >
            <Navigation size={16} /> Navigate
          </a>
          <a
            data-testid={`call-${booking.id}`}
            href={telHref}
            className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1a1a1a] text-[#1a1a1a] font-bold rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-[#1a1a1a] hover:text-white"
          >
            <Phone size={16} /> Call
          </a>
        </div>

        {!done && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            {booking.status === "scheduled" && (
              <Button
                data-testid={`run-start-${booking.id}`}
                onClick={() => onStatus(booking.id, "in_progress")}
                className="bg-[#fffbf0] border-2 border-[#ff6b35] text-[#ff6b35] hover:bg-[#ff6b35] hover:text-white font-bold rounded-lg col-span-1 h-10"
              >
                <Clock size={14} className="mr-1" /> Start
              </Button>
            )}
            {booking.status === "in_progress" && (
              <Button
                data-testid={`run-complete-${booking.id}`}
                onClick={() => onStatus(booking.id, "completed")}
                className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 text-white font-bold rounded-lg col-span-2 h-10"
              >
                <CheckCircle2 size={14} className="mr-1" /> Mark complete
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
